/**
 * URL query / postMessage 공통 테마 필드.
 * recsys 행동추천 carousel HTML·어시스턴트 딥링크와 동일 이름
 * (`primary`, `primary10`, `font_family`, `font_url`).
 */
export type AssistantTheme = {
  primary?: string;
  primary10?: string;
  background?: string;
  font_family?: string;
  font_url?: string;
};

const HEX_COLOR_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export const normalizeHexColor = (value?: string | null): string | undefined => {
  if (!value || typeof value !== 'string') {
    return undefined;
  }
  const text = value.trim();
  if (!HEX_COLOR_RE.test(text)) {
    return undefined;
  }
  if (text.length === 4) {
    return `#${text[1]}${text[1]}${text[2]}${text[2]}${text[3]}${text[3]}`.toLowerCase();
  }
  return text.toLowerCase();
};

export const normalizeFontUrl = (value?: string | null): string | undefined => {
  if (!value || typeof value !== 'string') {
    return undefined;
  }
  const text = value.trim();
  if (!/^https:\/\//i.test(text)) {
    return undefined;
  }
  if (/["'<>\n\r]/.test(text)) {
    return undefined;
  }
  return text;
};

export const normalizeFontFamily = (
  value?: string | null
): string | undefined => {
  if (!value || typeof value !== 'string') {
    return undefined;
  }
  const text = value.trim();
  if (!text || /[<>{};\n\r]/.test(text)) {
    return undefined;
  }
  return text;
};

const firstQueryValue = (
  value: string | string[] | undefined
): string | undefined => {
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
};

/**
 * Next.js router/pageProps query 또는 URLSearchParams 에서 테마를 읽는다.
 * 값이 없거나 무효면 해당 키는 생략.
 */
export const themeFromQuery = (
  query:
    | Record<string, string | string[] | undefined>
    | URLSearchParams
    | null
    | undefined
): AssistantTheme => {
  if (!query) {
    return {};
  }

  const get = (key: string): string | undefined => {
    if (typeof (query as URLSearchParams).get === 'function') {
      return (query as URLSearchParams).get(key) || undefined;
    }
    return firstQueryValue(
      (query as Record<string, string | string[] | undefined>)[key]
    );
  };

  const primary = normalizeHexColor(get('primary'));
  const primary10 = normalizeHexColor(get('primary10'));
  const background = normalizeHexColor(get('background'));
  const font_family = normalizeFontFamily(
    get('font_family') || get('fontFamily')
  );
  const font_url = normalizeFontUrl(get('font_url') || get('fontUrl'));

  return {
    ...(primary ? { primary } : {}),
    ...(primary10 ? { primary10 } : {}),
    ...(background ? { background } : {}),
    ...(font_family ? { font_family } : {}),
    ...(font_url ? { font_url } : {}),
  };
};

/** postMessage / SDK chatAssistant 객체를 검증된 테마로 정규화. */
export const themeFromAssistantConfig = (
  config?: Record<string, any> | null
): AssistantTheme => {
  if (!config) {
    return {};
  }
  const primary = normalizeHexColor(config.primary);
  const primary10 = normalizeHexColor(config.primary10);
  const background = normalizeHexColor(config.background);
  const font_family = normalizeFontFamily(
    config.font_family || config.fontFamily
  );
  const font_url = normalizeFontUrl(config.font_url || config.fontUrl);
  return {
    ...(primary ? { primary } : {}),
    ...(primary10 ? { primary10 } : {}),
    ...(background ? { background } : {}),
    ...(font_family ? { font_family } : {}),
    ...(font_url ? { font_url } : {}),
  };
};

export const paletteFromTheme = (
  theme: AssistantTheme
): Record<string, string> => {
  const out: Record<string, string> = {};
  if (theme.primary) {
    out.primary = theme.primary;
  }
  if (theme.primary10) {
    out.primary10 = theme.primary10;
  }
  if (theme.background) {
    out.background = theme.background;
  }
  return out;
};
